import {
  InfoPopover, InfoPopoverPlacementValues, ToggleField, ToggleFieldLayoutValues,
} from '../../../shared/components';
import customIcons from '../../../utils/icons';
import { HmrcDataToggleTooltip } from '../hmrcDataToggle.type';

export interface HmrcDataToggleProps {
  isOn: boolean,
  isExpanded: boolean,
  tooltip: HmrcDataToggleTooltip | null,
  onChange: (isOn: boolean) => void,
  onExpand: () => void,
}

function TooltipContent({ tooltip }: Readonly<{ tooltip: HmrcDataToggleTooltip }>) {
  return (
    <div className="flex flex-col gap-2">
      {tooltip.text.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      <a
        href={tooltip.link}
        target="_blank"
        rel="noopener noreferrer"
        className="self-start font-bold underline hover:text-primary-hover"
      >
        {tooltip.linkText}
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
    </div>
  );
}

function CollapsedHmrcDataToggle({ onExpand }: Readonly<Pick<HmrcDataToggleProps, 'onExpand'>>) {
  return (
    <div id="hmrc-data-toggle" className="border-y border-pipgrey-light py-2">
      <button
        type="button"
        title="HMRC Data"
        aria-label="Expand the menu to change HMRC data"
        onClick={onExpand}
        className="flex w-full justify-center p-2 rounded-lg text-2xl text-piptext-body cursor-pointer hover:bg-gray-100"
      >
        {customIcons.databaseIcon}
      </button>
    </div>
  );
}

function ExpandedHmrcDataToggle({
  isOn, tooltip, onChange,
}: Readonly<Pick<HmrcDataToggleProps, 'isOn' | 'tooltip' | 'onChange'>>) {
  return (
    <div id="hmrc-data-toggle" className="flex items-start gap-3 p-3 border-y border-pipgrey-light">
      <span aria-hidden="true" className="flex text-2xl text-piptext-body">{customIcons.databaseIcon}</span>
      <div className="flex flex-col gap-2 min-w-0">
        <div className="flex items-center gap-1">
          <span className="font-sans text-base font-semibold leading-[22.4px] text-piptext-default">HMRC Data</span>
          {tooltip && (
            <InfoPopover label="About HMRC data" placement={InfoPopoverPlacementValues.Right}>
              <TooltipContent tooltip={tooltip} />
            </InfoPopover>
          )}
        </div>
        <ToggleField
          label={isOn ? 'On' : 'Off'}
          aria-label="HMRC Data"
          layout={ToggleFieldLayoutValues.Inline}
          isSelected={isOn}
          onChange={onChange}
        />
      </div>
    </div>
  );
}

export default function HmrcDataToggle({
  isOn, isExpanded, tooltip, onChange, onExpand,
}: Readonly<HmrcDataToggleProps>) {
  return isExpanded
    ? <ExpandedHmrcDataToggle isOn={isOn} tooltip={tooltip} onChange={onChange} />
    : <CollapsedHmrcDataToggle onExpand={onExpand} />;
}
